<template>
    <div class="row">
        <div class="col-sm-12 mb-3" >
            <div class="d-flex flex-column gap-3 text-center align-items-center">
	            <i class="bi w-auto h-auto fs-large bi-shield-shaded"></i>
                <h3 class="fw-semi-bold m-0">Two Factor Authentication Recovery Codes</h3>
                <p class="text-secondary m-0">Use these recovery codes if you don't have access to the Two Factor Authentication Source.</p>
            </div>
        </div>

        <div ref="viewRecoveryCodeBlock" class="col-12 d-flex flex-column justify-content-center align-items-center">
	        <div class="text-center mb-3">
		        <div class="mb-2 d-flex justify-content-between" v-if="recoveryCodesTime && recoveryCodesLeft > -1" >
			        <span class="badge badge-dark mt-2 me-2" v-if="recoveryCodesTime"><span class="small ">Creation Time: </span><span class="fw-medium">{{ recoveryCodesTime }}</span></span>
			        <div class="badge badge-dark mt-2" v-if="recoveryCodesLeft > -1"><span class="small">Code Left: </span><span class="fw-medium">{{ recoveryCodesLeft }}/10</span></div>
		        </div>
		        <div class="row border shadow-sm rounded bg-light p-2">
			        <div class="row" v-html="recoveryCodesHTMLString"></div>
		        </div>
	        </div>
            <div class="d-flex flex-row align-items-center justify-content-center gap-3 text-center ">
	            <button class="btn btn-outline-secondary rounded-pill py-6" @click="printRecoveryCode">Print Code</button>
	            <button class="btn btn-outline-secondary rounded-pill py-6" @click="downloadRecoveryCode">Download Code</button>
	            <button class="btn btn-outline-secondary rounded-pill py-6" @click="copyRecoveryCode">Copy Code</button>
                <button class="btn btn-dark rounded-pill py-2 px-3" @click="close">Close</button>
            </div>
        </div>
    </div>

</template>

<script setup>
import { computed, watch, ref, onMounted } from "vue";
import {useAppHelpers} from "@/composables/useAppHelpers.js";
import { useAuthStore } from "@/stores/auth-store.js";
import { useToast } from "@/composables/useToast.js";
import apiClient from "@/utils/axios.js";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager.js";

const emits = defineEmits(['closeViewRecoveryCode']);

const auth = useAuthStore();
const helper = useAppHelpers();
const { displayToast } = useToast();

const viewRecoveryCodeBlock = ref(null);
const recoveryCodes = ref([]);
const recoveryCodesTime = ref(null);
const recoveryCodesLeft = ref(-1);

const recoveryCodesHTMLString = computed(() => {
	return recoveryCodes.value.map(code => `<div class="col-6 recovery-code ${code['is_used'] ? 'used-recovery-code text-danger' : ''}">${code['code']}</div>`).join('<br>');
});

const recoveryCodesCopyString = computed(() => {
	return recoveryCodes.value.map(code => `${code['is_used'] ? '' : code['code']}`).join("\n");
});

const copyRecoveryCode = () => {
	helper.copyText(recoveryCodesCopyString.value);
	displayToast('Recovery codes copied to clipboard.', 'outline-success', 5000);
}

const printRecoveryCode = () => {
	const printWindow = window.open('', '', 'width=600,height=600');
	printWindow.document.write('<html lang="en"><head><title>Print Recovery Codes</title></head><body>');
	printWindow.document.write('<h3>Two Factor Authentication Recovery Codes</h3>');
	printWindow.document.write('<p>Use these recovery codes if you don\'t have access to the Two Factor Authentication Source.</p>');
	printWindow.document.write('<pre>' + recoveryCodesCopyString.value + '</pre>');
	printWindow.document.write('</body></html>');
	printWindow.document.close();
	printWindow.print();
}

const downloadRecoveryCode = () => {
	const blob = new Blob([recoveryCodesCopyString.value], { type: 'text/plain' });
	const link = document.createElement('a');
	link.href = URL.createObjectURL(blob);
	link.download = 'my_meds_recovery_codes.txt';
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	displayToast('Recovery codes downloaded successfully.', 'outline-success', 5000);
}

const user = ref(auth.user);
watch(() => auth.user, (newVal) => {
	user.value = newVal;
});

onMounted(async () => {
	await getRecoveryCode();
});

const getRecoveryCode = async () => {
	try{
		helper.loaderOn(viewRecoveryCodeBlock.value);
		const response = await apiClient.post('/auth/user/getRecoveryCodes', {
		});

		if(response.status >= 200 && response.status < 300){
			recoveryCodes.value = response.data.recovery_codes;
			recoveryCodesTime.value = response.data.created_at;
			recoveryCodesLeft.value = response.data.codes_left;
		}
		helper.loaderOff(viewRecoveryCodeBlock.value);
	}
	catch(error){
		recoveryCodes.value = [];
		let errorObj = await useHttpErrorManager().handleError(error);
		let message = errorObj['full_message'];
		displayToast(message, 'outline-danger', 8000);
		helper.loaderOff(viewRecoveryCodeBlock.value);
	}
}

const close = async () => {
	emits('closeViewRecoveryCode');
}

defineExpose({
    close
});
</script>

<style lang="scss">
.recovery-code{
	font-family: monospace;
}
.used-recovery-code{
	text-decoration: line-through;
}
</style>
